import { configuratorConstants, ExtraChargeType, defaultFeedInTariffSettings, type FeedInTariffSettings, type FeedInTariffTier } from "$lib/constants";
import type { BatteryData } from "$lib/products/batteries";
import { consumptionCharacteristics, matrix_ev_quote, matrix_pv_yield } from "$lib/products/misc";
import type { ModuleData } from "$lib/products/modules";
import { type Configurator, useConfiguratorStore } from "$lib/store/configurator.store";
import { useConfiguratorSummaryStore } from "$lib/store/configurator.summary.store";
import { calculateFinalPrice, calculatePreisWithDiscount } from "$lib/utils/product.calculation.helper";
import { get } from "svelte/store";
import type { Product } from "../server/database/drizzle-schemas.ts";

const SYSTEM_LOSS_FACTOR = 0.98;
const MAX_SPECIFIC_YIELD = 940;

export class SummaryBuilder {
    private configuratorStore = get(useConfiguratorStore());
    private specificYield: number;
    private storageSize: number;
    private ratioBatConsumption: number;
    private pvYield: number;
    private ratioPvConsumption: number;
    private evQuoteHouse: number = 0;
    private evHouse: number = 0;
    private selfSufficiencyHouse: number = 0;
    private householdConsumption: number = 0;
    private evQuoteHeatPump: number = 0;
    private evHeatPump: number = 0;
    private selfSufficiencyHeatPump: number = 0;
    private heatPumpConsumption: number = 0;
    private evQuoteECar: number = 0;
    private evECar: number = 0;
    private selfSufficiencyECar: number = 0;
    private eCarConsumption: number = 0;
    private evQuoteTotal: number = 0;
    private evTotal: number = 0;
    private selfSufficiencyTotal: number = 0;
    private totalConsumption: number = 0;
    private refundRate: number = 0;
    private yearlyRefund: number = 0;
    private yearlySavings: number = 0;
    private yearlyGain: number = 0;
    private totalGain: number = 0;
    private energyGenerationCost: number = 0;
    private amortizationPeriod: number = 0;
    private roi: number = 0;
    private totalCost: number = 0;
    private averageElectricityPrice: number = 0;
    private totalElectricityCost: number = 0;
    private isFullFeedIn: boolean;
    private feedInTariffSettings: FeedInTariffSettings;

    private readonly RESVALUE_FACTOR = 0.8;
    private defaultProducts?: Array<Product>;

    constructor(
        moduleData: ModuleData,
        batteryData: BatteryData | null,
        isFullFeedIn: boolean = false,
        feedInTariffSettings?: FeedInTariffSettings,
        configuratorStore?: Configurator,
        defaultProducts?: Array<Product>,
    ) {
        this.configuratorStore = get(useConfiguratorStore(configuratorStore));
        this.defaultProducts = defaultProducts;

        this.isFullFeedIn = isFullFeedIn;
        this.feedInTariffSettings = feedInTariffSettings ?? defaultFeedInTariffSettings;
        // spez. Ertrag
        this.specificYield = this.calculateSpecificYield(moduleData);

        // Speichergröße
        this.storageSize = this.calculateStorageSize(batteryData);
        this.ratioBatConsumption = this.calculateRatioBatConsumption(this.storageSize);

        // PV Ertrag
        this.pvYield = this.configuratorStore.pvSize * this.specificYield;

        // Haushaltsverbrauch
        this.householdConsumption = this.configuratorStore.electricityConsumption.electricityConsumption;

        // PV-zu-Verbrauch Index (0–165), exakt nach Berechnungsleitfaden
        this.ratioPvConsumption = this.clampIndex(
            Math.floor((this.configuratorStore.pvSize / (this.householdConsumption / 1000)) * 10)
        );

        if (!isFullFeedIn) {
            // Haus
            this.calculateHouseholdPVYield();

            // WP
            this.calculateHeatPumpYield();

            // E-Car
            this.calculateECarYield();
        }

        // Total values
        this.calculateTotalValues();

        // Refund rate
        this.refundRate = this.calculateRefundRate();

        // Financial calculations
        this.calculateFinancials();

        const configuratorSummaryStore = useConfiguratorSummaryStore();
        configuratorSummaryStore.update((summary) => {
            return Object.assign(summary, {
                yield: {
                    specificYield: this.specificYield,
                    storageSize: this.storageSize,
                    pvYield: this.pvYield
                },
                economy: {
                    refundRate: this.refundRate,
                    yearlyRefund: this.yearlyRefund,
                    yearlySavings: this.yearlySavings,
                    totalGain: this.totalGain,
                    totalCost: this.totalCost,
                    averageElectricityPrice: this.averageElectricityPrice,
                    totalElectricityCost: this.totalElectricityCost,
                    roi: this.roi,
                    amortizationPeriod: this.amortizationPeriod,
                    energyGenerationCost: this.energyGenerationCost
                },
                consumption: {
                    household: {
                        evQuote: this.evQuoteHouse,
                        ev: this.evHouse,
                        selfSufficiency: this.selfSufficiencyHouse,
                        consumption: this.householdConsumption
                    },
                    heatPump: {
                        evQuote: this.evQuoteHeatPump,
                        ev: this.evHeatPump,
                        selfSufficiency: this.selfSufficiencyHeatPump,
                        consumption: this.heatPumpConsumption
                    },
                    eCar: {
                        evQuote: this.evQuoteECar,
                        ev: this.evECar,
                        selfSufficiency: this.selfSufficiencyECar,
                        consumption: this.eCarConsumption
                    },
                    total: {
                        evQuote: this.evQuoteTotal,
                        ev: this.evTotal,
                        selfSufficiency: this.selfSufficiencyTotal,
                        consumption: this.totalConsumption
                    }
                }
            });
        });
    }

    private calculateHouseholdPVYield(): void {
        const evFaktor = this.configuratorStore.characteristicsConsumption.characteristicsConsumption ? consumptionCharacteristics.find(
            (c) => c.id === this.configuratorStore.characteristicsConsumption.characteristicsConsumption
        )!.householdValue : 0;

        this.evQuoteHouse = Math.trunc(
            matrix_ev_quote[165 - this.ratioBatConsumption][this.ratioPvConsumption] * evFaktor
        );
        this.evHouse = parseFloat((this.pvYield * (this.evQuoteHouse / 100)).toFixed(2));
        this.selfSufficiencyHouse = (this.evHouse / this.householdConsumption) * 100;

        if (this.selfSufficiencyHouse > configuratorConstants.autonomy.maxHousehold) {
            this.selfSufficiencyHouse = configuratorConstants.autonomy.maxHousehold;
            this.evHouse = parseFloat(
                (this.householdConsumption * (this.selfSufficiencyHouse / 100)).toFixed(2)
            );
            this.evQuoteHouse = parseFloat(((this.evHouse / this.pvYield) * 100).toFixed(2));
        }
    }

    private calculateSpecificYield(moduleData: ModuleData): number {
        let specificYield = 0;

        this.configuratorStore.roofSurfaces.modules.forEach((module) => {
            const orientationIndex = Math.round(module.roofOrientation / 10);
            let slopeIndex = 9;

            if (module.roofSlope <= 45) {
                const slope = Math.max(module.roofSlope, 5);
                const roundedSlope = slope % 5 === 0
                    ? Math.floor(slope / 5) * 5
                    : Math.floor(slope / 5) * 5 + 5;
                slopeIndex = roundedSlope / 5 - 1;
            }

            specificYield += this.configuratorStore.insolation.insolation *
                (matrix_pv_yield[orientationIndex][slopeIndex] / 100) *
                ((module.displayModule ?? 1) / this.configuratorStore.amountOfModule);
        });

        specificYield = parseFloat(
            (specificYield * SYSTEM_LOSS_FACTOR * (moduleData.efficiencyFactor ?? 1)).toFixed(2)
        );

        return Math.min(specificYield, MAX_SPECIFIC_YIELD);
    }

    private calculateStorageSize(batteryData: BatteryData | null): number {
        if (!batteryData) return 0;
        return this.configuratorStore.amountOfBattery * batteryData.batteryCapacity;
    }

    private calculateRatioBatConsumption(storageSize: number): number {
        return this.clampIndex(
            Math.floor(
                (storageSize / (this.configuratorStore.electricityConsumption.electricityConsumption / 1000)) * 10
            )
        );
    }

    /** Ensure matrix indices remain within 0-165 */
    private clampIndex(value: number): number {
        return Math.min(165, Math.max(0, value));
    }

    private calculateHeatPumpYield(): void {
        if (!this.configuratorStore.heatPumpConsume) {
            this.evQuoteHeatPump = 0;
            this.evHeatPump = 0;
            this.selfSufficiencyHeatPump = 0;
            return;
        }

        this.heatPumpConsumption =
            (this.configuratorStore.heatPumpConsume ?? 0) >
                configuratorConstants.heatPump.minYearlyConsumption
                ? this.configuratorStore.heatPumpConsume
                : 0;

        const ratioBatConsumptionHeatPump = this.clampIndex(
            Math.floor((this.storageSize / (this.heatPumpConsumption / 1000)) * 10)
        );
        const ratioPvConsumptionHeatPump = this.clampIndex(
            Math.floor((this.pvYield / (this.heatPumpConsumption / 1000)) * 10)
        );

        const evFaktorHeatPump = consumptionCharacteristics.find(
            (c) => c.id === this.configuratorStore.characteristicsConsumption.characteristicsConsumption
        )!.heatPumpValue;

        this.evQuoteHeatPump = Math.trunc(
            matrix_ev_quote[165 - ratioBatConsumptionHeatPump][ratioPvConsumptionHeatPump] * evFaktorHeatPump
        );

        if (!this.evQuoteHeatPump) {
            this.evQuoteHeatPump = 3;
        }

        this.evHeatPump = parseInt((this.pvYield * (this.evQuoteHeatPump / 100)).toFixed(2));
        this.selfSufficiencyHeatPump = parseInt(((this.evHeatPump / this.heatPumpConsumption) * 100).toFixed(2));

        if (this.selfSufficiencyHeatPump > configuratorConstants.autonomy.maxHeatPump) {
            this.selfSufficiencyHeatPump = configuratorConstants.autonomy.maxHeatPump;
            this.evHeatPump = parseInt((this.heatPumpConsumption * (this.selfSufficiencyHeatPump / 100)).toFixed(2));
            this.evQuoteHeatPump = parseInt(((this.evHeatPump / this.pvYield) * 100).toFixed(2));
        }
    }

    private calculateECarYield(): void {
        if (!this.configuratorStore.eCarConsume) {
            this.evQuoteECar = 0;
            this.evECar = 0;
            this.selfSufficiencyECar = 0;
            this.eCarConsumption = 0;
            return;
        }

        this.eCarConsumption =
            (this.configuratorStore.eCarConsume / 100) * configuratorConstants.electricVehicle.consumption;
        const ratioBatConsumptionECar = this.clampIndex(
            Math.floor((this.storageSize / (this.eCarConsumption / 1000)) * 10)
        );
        const ratioPvConsumptionECar = this.clampIndex(
            Math.floor((this.configuratorStore.pvSize / (this.eCarConsumption / 1000)) * 10)
        );

        const evFaktorECar = consumptionCharacteristics.find(
            (c) => c.id === this.configuratorStore.characteristicsConsumption.characteristicsConsumption
        )!.electricCarValue;

        try {
            this.evQuoteECar = Math.trunc(
                matrix_ev_quote[165 - ratioBatConsumptionECar][ratioPvConsumptionECar] * evFaktorECar
            );
        } catch {
            this.evQuoteECar = 3;
        }

        this.evECar = parseInt((this.pvYield * (this.evQuoteECar / 1000)).toFixed(2));
        this.selfSufficiencyECar = parseInt(((this.evECar / this.eCarConsumption) * 100).toFixed(2));

        if (this.selfSufficiencyECar > configuratorConstants.electricVehicle.maxAutonomy) {
            this.selfSufficiencyECar = configuratorConstants.electricVehicle.maxAutonomy;
            this.evECar = parseInt((this.eCarConsumption * (this.selfSufficiencyECar / 100)).toFixed(2));
            this.evQuoteECar = parseInt(((this.evECar / this.pvYield) * 100).toFixed(2));
        }
    }

    private calculateTotalConsumption(): number {
        return this.householdConsumption + this.heatPumpConsumption + this.eCarConsumption;
    }

    private calculateTotalValues(): void {
        this.totalConsumption = this.calculateTotalConsumption();
        const hasActiveConsumers = this.heatPumpConsumption > 0 || this.eCarConsumption > 0;

        if (hasActiveConsumers) {
            const ratioBatConsumptionTotal = this.clampIndex(
                Math.floor((this.storageSize / (this.totalConsumption / 1000)) * 10)
            );
            const ratioPvConsumptionTotal = this.clampIndex(
                Math.floor((this.configuratorStore.pvSize / (this.totalConsumption / 1000)) * 10)
            );

            const evFaktor = consumptionCharacteristics.find(
                (c) => c.id === this.configuratorStore.characteristicsConsumption.characteristicsConsumption
            )!.householdValue;

            this.evQuoteTotal = Math.trunc(
                matrix_ev_quote[165 - ratioBatConsumptionTotal][ratioPvConsumptionTotal] * evFaktor
            );
            this.evTotal = parseInt((this.pvYield * (this.evQuoteTotal / 100)).toFixed(2));
            this.selfSufficiencyTotal = parseInt(((this.evTotal / this.totalConsumption) * 100).toFixed(2));

            const maxSelfSufficiency = this.heatPumpConsumption > 0
                ? configuratorConstants.autonomy.maxHeatPump
                : configuratorConstants.autonomy.maxHousehold;

            if (this.selfSufficiencyTotal > maxSelfSufficiency) {
                this.selfSufficiencyTotal = maxSelfSufficiency;
                this.evTotal = parseInt((this.totalConsumption * (this.selfSufficiencyTotal / 100)).toFixed(2));
                this.evQuoteTotal = parseInt(((this.evTotal / this.pvYield) * 100).toFixed(2));
            }
        } else {
            this.evQuoteTotal = this.evQuoteHouse;
            this.selfSufficiencyTotal = this.selfSufficiencyHouse;
            this.evTotal = this.evHouse;
        }

        // Enforce final quote limits
        this.evQuoteTotal = this.heatPumpConsumption > 0 ? Math.min(this.evQuoteTotal, 18) : this.evQuoteTotal;
        this.selfSufficiencyTotal = Math.min(this.selfSufficiencyTotal, this.heatPumpConsumption > 0 ? 55 : 80);
    }

    private calculateRefundRate(): number {
        const pvSize = this.configuratorStore.pvSize;
        if (pvSize <= 0) return 0;

        const config = this.isFullFeedIn
            ? this.feedInTariffSettings.full
            : this.feedInTariffSettings.partial;

        const maxRefundLimit = config.refundTiers.length > 0
            ? Math.max(...config.refundTiers.map(t => t.upperLimit))
            : 0;
        const maxAzwLimit = config.azwTiers.length > 0
            ? Math.max(...config.azwTiers.map(t => t.upperLimit))
            : 0;

        if (pvSize <= maxRefundLimit && config.refundTiers.length > 0) {
            return this.calculateWeightedRate(pvSize, config.refundTiers);
        }
        if (pvSize <= maxAzwLimit && config.azwTiers.length > 0) {
            return this.calculateWeightedRate(pvSize, config.azwTiers);
        }
        return config.sonstAzw;
    }

    private calculateWeightedRate(pvSize: number, tiers: FeedInTariffTier[]): number {
        const sorted = [...tiers].sort((a, b) => a.upperLimit - b.upperLimit);
        let total = 0;
        let prev = 0;

        for (const tier of sorted) {
            if (pvSize <= tier.upperLimit) {
                total += (pvSize - prev) * tier.rate;
                break;
            }
            total += (tier.upperLimit - prev) * tier.rate;
            prev = tier.upperLimit;
        }

        return total / pvSize;
    }

    private calculateYearlyRefund(): number {
        return (this.pvYield - this.evTotal) * this.refundRate;
    }

    private calculateElectricityCosts(): void {
        if (this.isFullFeedIn) {
            this.totalElectricityCost = 0;
            this.averageElectricityPrice = 0;
            return;
        }

        let totalCost = 0;
        let currentPrice = this.configuratorStore.electricityConsumption.currentPrice;
        const duration = this.configuratorStore.electricityConsumption.duration;
        const yearlyConsumption = this.configuratorStore.electricityConsumption.electricityConsumption;
        const priceIncrease = (
            this.configuratorStore.electricityConsumption.priceIncreaseRate +
            this.configuratorStore.electricityConsumption.inflation
        ) / 100;

        for (let i = 1; i <= duration; i++) {
            totalCost += yearlyConsumption * currentPrice;
            currentPrice *= (1 + priceIncrease);
        }

        this.totalElectricityCost = totalCost;
        this.averageElectricityPrice = totalCost / (yearlyConsumption * duration);
    }

    private calculateFinancials(): void {
        this.calculateElectricityCosts();
        this.yearlyRefund = this.calculateYearlyRefund();
        this.yearlySavings = this.evTotal * this.averageElectricityPrice;
        this.yearlyGain = this.yearlySavings + this.yearlyRefund;
        this.totalGain = this.yearlyGain * this.configuratorStore.electricityConsumption.duration;
        // Final Price
        this.totalCost = this.calculateFinalTotal();
        this.energyGenerationCost = this.totalCost / (this.pvYield * this.configuratorStore.electricityConsumption.duration);
        // Amortisation = Investition / (jährliche Ersparnis + jährliche Vergütung)
        this.amortizationPeriod = this.totalCost / (this.yearlySavings + this.yearlyRefund);
        this.roi = (((this.totalGain - this.totalCost * (1 - this.RESVALUE_FACTOR)) / this.totalCost) * 100) /
            this.configuratorStore.electricityConsumption.duration;
    }

    private calculateFinalTotal(): number {
        return calculatePreisWithDiscount(
            calculateFinalPrice(this.defaultProducts) ?? 0,
            this.configuratorStore.discount,
            this.configuratorStore.extraCharge ?? 0,
            this.configuratorStore.extraChargeType ?? ExtraChargeType.EURO
        );
    }
}